/**
 * Utility functions for parsing camp dates_available and extracting start dates
 * Used for age filtering calculations
 */

/**
 * Extract start date from a date string
 * Handles various formats:
 * - "June 15-20, 2025"
 * - "June 15, 2025 - June 20, 2025"
 * - "Summer 2025"
 * - "2025-06-15"
 * - "06/15/2025"
 * @param dateString - Date string from dates_available array
 * @returns Parsed Date object for the start date, or null if cannot parse
 */
export function extractStartDate(dateString: string): Date | null {
  if (!dateString || typeof dateString !== 'string') return null;

  const trimmed = dateString.trim();
  if (!trimmed) return null;

  // Try ISO date format first (YYYY-MM-DD)
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const date = new Date(isoMatch[0]);
    if (!isNaN(date.getTime())) return date;
  }

  // Try MM/DD/YYYY or M/D/YYYY format
  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (slashMatch) {
    const [, month, day, year] = slashMatch;
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    if (!isNaN(date.getTime())) return date;
  }

  // Try "Month Day-Year, Year" or "Month Day - Month Day, Year"
  // e.g., "June 15-20, 2025" or "June 15 - June 20, 2025"
  const monthDayRangeMatch = trimmed.match(/^([A-Za-z]+)\s+(\d{1,2})(?:\s*-\s*(?:\d{1,2}|[A-Za-z]+\s+\d{1,2}))?[,\s]+(\d{4})/);
  if (monthDayRangeMatch) {
    const [, monthName, day, , year] = monthDayRangeMatch;
    const monthIndex = getMonthIndex(monthName);
    if (monthIndex !== -1 && day && year) {
      const date = new Date(parseInt(year), monthIndex, parseInt(day));
      if (!isNaN(date.getTime())) return date;
    }
  }

  // Try "Month Day, Year" format
  const monthDayMatch = trimmed.match(/^([A-Za-z]+)\s+(\d{1,2})[,\s]+(\d{4})/);
  if (monthDayMatch) {
    const [, monthName, day, year] = monthDayMatch;
    const monthIndex = getMonthIndex(monthName);
    if (monthIndex !== -1 && day && year) {
      const date = new Date(parseInt(year), monthIndex, parseInt(day));
      if (!isNaN(date.getTime())) return date;
    }
  }

  // Try "Season Year" format (e.g., "Summer 2025")
  // Use a reasonable default date for the season
  const seasonMatch = trimmed.match(/^(Spring|Summer|Fall|Autumn|Winter)\s+(\d{4})/i);
  if (seasonMatch) {
    const [, season, year] = seasonMatch;
    const yearNum = parseInt(year);
    let month = 6; // Default to June (Summer)
    
    if (/spring/i.test(season)) month = 3; // March
    else if (/summer/i.test(season)) month = 6; // June
    else if (/fall|autumn/i.test(season)) month = 9; // September
    else if (/winter/i.test(season)) month = 12; // December
    
    const date = new Date(yearNum, month - 1, 1); // First day of the month
    if (!isNaN(date.getTime())) return date;
  }

  // Try parsing as-is (may work for some formats)
  const directParse = new Date(trimmed);
  if (!isNaN(directParse.getTime()) && directParse.getFullYear() >= 2020) {
    return directParse;
  }

  return null;
}

/**
 * Get month index (0-11) from month name
 */
function getMonthIndex(monthName: string): number {
  const months = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'
  ];
  
  const normalized = monthName.toLowerCase().trim();
  const index = months.findIndex(m => m.startsWith(normalized) || normalized.startsWith(m));
  return index;
}

/**
 * Extract all start dates from a camp's dates_available array
 * @param datesAvailable - Array of date strings from camp.dates_available
 * @returns Array of Date objects representing start dates, or empty array
 */
export function extractAllStartDates(datesAvailable: string[] | null | undefined): Date[] {
  if (!datesAvailable || !Array.isArray(datesAvailable)) return [];
  
  const startDates: Date[] = [];
  
  for (const dateString of datesAvailable) {
    const startDate = extractStartDate(dateString);
    if (startDate) {
      startDates.push(startDate);
    }
  }
  
  return startDates;
}

/**
 * Get the earliest start date from a camp's dates_available
 * Used for age filtering when camp has multiple date ranges
 * @param datesAvailable - Array of date strings from camp.dates_available
 * @returns Earliest start Date, or null if cannot parse
 */
export function getEarliestStartDate(datesAvailable: string[] | null | undefined): Date | null {
  const startDates = extractAllStartDates(datesAvailable);
  if (startDates.length === 0) return null;
  
  return new Date(Math.min(...startDates.map(d => d.getTime())));
}



